import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import { Button, Form } from 'reactstrap';
import { reduxForm } from 'redux-form';
import PhoneNumberField, {
    normalizePhone,
} from 'components/fields/PhoneNumberField';
import identityClient from '../../../clients/identityClient';
import {
    shouldBlockLinkingPhoneBeforeVerify,
} from '../../../helpers/accountMatchingUtils';
import errorMessages, { toErrorAlertState } from '../../../helpers/errorMessages';
import { error as err, registrationSteps } from '../PreSignUp';
import ErrorAlert from '../PreSignUp/ErrorAlert';

const MobilePromptMatchFound = ({
    navigate,
    handleSubmit,
    registrationFields,
    isRequestPending,
}) => {
    const [error, setError] = useState(err);
    const [isLoading, setIsLoading] = useState(false);
    const { email, hStreamId, isConfirmed, workflowType, firstName, lastName } = registrationFields || {};
    const isProvisioned = registrationFields?.isProvisioned === true;

    const onSendVerificationCode = async (values) => {
        setIsLoading(true);
        const { phone } = values;
        try {
            const normalizedPhone = normalizePhone(phone);
            const normalizedCurrentHstreamId = String(hStreamId || '').trim();
            const accountLookup = await identityClient.getAccountByPhoneNumber(
                normalizedPhone,
            );
            if (
                shouldBlockLinkingPhoneBeforeVerify(
                    accountLookup,
                    {
                        isProvisioned,
                        isConfirmed,
                        workflowType,
                    },
                )
            ) {
                setError({
                    errorFound: true,
                    errorMessage:
                        errorMessages.phoneOnAnotherAccountLinking.message,
                    errorType: errorMessages.phoneOnAnotherAccountLinking.type,
                });
                return;
            }

            const hStreamIdForOtp = normalizedCurrentHstreamId;

            await identityClient.sendRegistrationOTP(
                '',
                hStreamIdForOtp,
                normalizedPhone,
                workflowType,
            );
            navigate(registrationSteps.VERIFICATIONCODE, {
                email,
                hStreamId: hStreamIdForOtp,
                phone: normalizedPhone,
                isConfirmed,
                isProvisioned,
                workflowType,
                firstName,
                lastName,
            });
        } catch (e) {
            setError(
                e.response?.data?.status === 400 || e.response?.data?.status === 409
                    ? toErrorAlertState(e.response?.data?.detail)
                    : {
                          errorFound: true,
                          errorMessage: errorMessages?.processing?.message,
                      },
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {error.errorFound && <ErrorAlert error={error} />}
            <section className='pt-3'>
                <p>
                    {' '}
                    Your phone number will be used for password recovery and will
                    not be used for any other purposes without your consent.{' '}
                </p>
            </section>
            <div
                className="position-relative"
                aria-busy={isLoading || undefined}
            >
                <BlockingSpinnerOverlay
                    show={isLoading}
                    ariaLabel="Loading..."
                />
                <Form onSubmit={handleSubmit(onSendVerificationCode)}>
                    <PhoneNumberField
                        label="Mobile Number"
                        name="phone"
                        id="mobilePromptMatchFoundPhone"
                        disabled={isRequestPending || isLoading}
                        required
                    />
                    <PhoneNumberField
                        label="Confirm Mobile Number"
                        name="confirm-phone"
                        disabled={isRequestPending || isLoading}
                        required
                        confirm
                    />
                    <Button
                        id="sendVerificationCodeMatchFound"
                        color="primary"
                        block
                        type="submit"
                        disabled={isRequestPending || isLoading}
                    >
                        {isLoading ? 'Loading...' : 'Send Verification Code'}
                    </Button>
                </Form>
            </div>
        </>
    );
};

MobilePromptMatchFound.propTypes = {
    navigate: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    registrationFields: PropTypes.shape({
        email: PropTypes.string,
        hStreamId: PropTypes.string,
        isProvisioned: PropTypes.bool,
        isConfirmed: PropTypes.bool,
        workflowType: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
    }),
    isRequestPending: PropTypes.bool,
};

MobilePromptMatchFound.defaultProps = {
    registrationFields: {},
    isRequestPending: false,
};

export default reduxForm({
    form: 'mobilePromptMatchFoundForm',
})(MobilePromptMatchFound);
